import {
  AutocompleteInputChangeReason,
  ListItem,
  ListItemText,
} from "@mui/material";
import { SxProps } from "@mui/system";
import { SyntheticEvent, useCallback, useMemo, useState } from "react";
import useWorkTitleAutocomplete, {
  WorkTitleOption,
} from "../../../../../hooks/useWorkTitleAutocomplete";
import AutocompleteGroupedList from "../../AutocompleteGroupedList/AutocompleteGroupedList";
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import AutocompleteBase from "../autocompleteBase/AutocompleteBase";
import {
  dedupeWorkTitleValues,
  normalizeSelectedWorkTitleValues,
} from "./workTitleValueUtils";

interface Props {
  value?: WorkTitleOption[];
  onChange?: (workTitles: WorkTitleOption[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  limitTags?: number;
  groupByFieldOfWork?: boolean;
  fetchAll?: boolean;
  sx?: SxProps;
  placeholder?: string;
}

// TODO: Refactor this component, it has become too complex
const WorkTitleAutocomplete = ({
  value,
  onChange,
  multiple = true,
  disabled = false,
  fullWidth = true,
  size = "small",
  limitTags,
  groupByFieldOfWork = true,
  fetchAll = false,
  sx = {},
  placeholder,
}: Props) => {
  const [inputValue, setInputValue] = useState<string>("");

  const { workTitleOptions, setWorkTitleSearchTerm, resetSearch, isLoading } =
    useWorkTitleAutocomplete({
      groupByFieldOfWork,
      fetchAll,
    });

  const normalizedValue = useMemo(() => {
    const normalizedValues = normalizeSelectedWorkTitleValues(
      value,
      workTitleOptions,
    );

    return normalizedValues.length > 0
      ? normalizedValues
      : multiple
        ? []
        : null;
  }, [value, workTitleOptions, multiple]);

  const handleInputChange = (
    _: SyntheticEvent,
    newValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "reset") {
      return;
    }

    setInputValue(newValue);

    // When the full list is already loaded, Autocomplete filters it by
    // inputValue. Server-side search is only used when fetchAll is false.
    if (fetchAll) {
      return;
    }

    if (newValue.trim()) {
      setWorkTitleSearchTerm(newValue);
    } else {
      resetSearch();
    }
  };

  const onClose = () => {
    setInputValue("");
    resetSearch();
  };

  const handleChange = useCallback(
    (newValue: WorkTitleOption | WorkTitleOption[] | null) => {
      if (onChange) {
        setInputValue("");
        resetSearch();
        if (multiple) {
          let values: WorkTitleOption[] | null = null;

          if (Array.isArray(newValue) && newValue.length > 0) {
            values = normalizeSelectedWorkTitleValues(
              dedupeWorkTitleValues(newValue),
              workTitleOptions,
            );
          } else if (newValue && !Array.isArray(newValue)) {
            values = normalizeSelectedWorkTitleValues(
              [newValue],
              workTitleOptions,
            );
          }

          onChange(values);
        } else {
          const singleValue = Array.isArray(newValue)
            ? newValue[0] || null
            : newValue;
          onChange(singleValue ? [singleValue] : null);
        }
      }
    },
    [onChange, multiple, workTitleOptions, resetSearch],
  );

  const getOptionLabel = (option: WorkTitleOption) => option.name;
  const isOptionEqualToValue = (
    option: WorkTitleOption,
    val: WorkTitleOption,
  ) => option.id === val.id;

  return (
    <AutocompleteBase<WorkTitleOption>
      value={normalizedValue || []}
      onChange={(newValue) =>
        handleChange(newValue as WorkTitleOption | WorkTitleOption[] | null)
      }
      options={workTitleOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      limitTags={limitTags}
      size={size}
      loading={isLoading}
      loadingText={<LoadingSpinner />}
      groupBy={
        groupByFieldOfWork
          ? (option: WorkTitleOption) => option.fieldOfWork
          : undefined
      }
      renderGroup={
        groupByFieldOfWork
          ? ({ group, children }) => (
              <AutocompleteGroupedList
                key={group}
                group={group}
                children={children}
              />
            )
          : undefined
      }
      renderOption={(props, option: WorkTitleOption) => (
        <ListItem {...props} key={option.id}>
          <ListItemText>{option.name}</ListItemText>
        </ListItem>
      )}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      onClose={onClose}
      filterOptions={fetchAll ? undefined : (options) => options}
      placeholder={placeholder}
      sx={sx}
    />
  );
};

export default WorkTitleAutocomplete;
